import React from 'react';
import { Helmet } from 'react-helmet-async';
import { PageHeader } from './Services';
import { Award, HeartHandshake, Building2, Sprout, Quote } from 'lucide-react';
import { Link } from 'react-router-dom';
import CtaBanner from '../components/CtaBanner';
import PageMeta from '../components/PageMeta';
import { buildBreadcrumbLd } from '../lib/structuredData';

const values = [
  { icon: HeartHandshake, title: 'Show up, every time', text: 'A booking is a promise. Our clerks treat each one like their own job depends on it.' },
  { icon: Award, title: 'Reports that stand up in court', text: 'Time-stamped, photographic evidence — because deposit disputes are real.' },
  { icon: Building2, title: 'Built by agents, for agents', text: 'We have spent years inside lettings branches ourselves. We know exactly what slows you down.' },
  { icon: Sprout, title: 'Local clerks, fair pay', text: 'Our clerks earn the Living Wage minimum — happy clerks deliver better reports.' },
];

const About = () => {
  return (
    <div>
      <PageMeta
        title="About · Residential Support — A national UK property services company"
        description="The story behind Residential Support Ltd — a national outsourcing partner quietly handling check-in and check-out inventories, accompanied viewings, photography and floor plans for UK letting agencies."
        path="/about"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(
            buildBreadcrumbLd([
              { label: 'Home', path: '/' },
              { label: 'About', path: '/about' },
            ])
          )}
        </script>
      </Helmet>
      <PageHeader
        eyebrow="About us"
        title="A UK property services company."
        sub="A national outsourcing partner for UK letting agencies — quietly handling the check-in and check-out inventories, accompanied viewings, photography and floor plans that keep your branch moving."
      />

      <section className="sect bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <img
              src="https://images.unsplash.com/photo-1521791136064-7986c2920216?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwzfHxidXNpbmVzcyUyMGhhbmRzaGFrZXxlbnwwfHx8fDE3ODEwMTI2OTF8MA&ixlib=rb-4.1.0&q=85"
              alt="Team"
              className="w-full h-[480px] object-cover rounded-[24px]"
            />
          </div>
          <div className="lg:col-span-6">
            <div className="text-xs uppercase tracking-[0.2em] text-[var(--rs-green)] mb-3">Our story</div>
            <h2 className="h-display text-4xl md:text-5xl text-[var(--rs-ink)]">A national network, built for letting agencies that want their week back.</h2>
            <div className="mt-5 space-y-4 text-[var(--rs-ink)]/75 text-[1.02rem] leading-relaxed">
              <p>Residential Support exists because the agencies we work with were drowning in the small jobs that take a whole day to organise — and an hour to actually do. Inventories that come back late. Viewings that clash with valuations. Photography sessions no one in the office has the kit to run properly.</p>
              <p>So we built a national network of vetted, local UK clerks who quietly take that workload off your hands — every postcode, every property type, every service. We are the team behind the team: invisible to your clients, indispensable to your branch.</p>
              <p>We are not the biggest outsourcer in the UK. We just intend to be the one you actually trust.</p>
            </div>
            <div className="mt-7 flex gap-3"><Link to="/contact" className="btn-primary">Work with us</Link></div>
          </div>
        </div>
      </section>

      <section className="sect bg-[var(--rs-cream)]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-xs uppercase tracking-[0.2em] text-[var(--rs-green)] mb-3">What we stand for</div>
          <h2 className="h-display text-4xl md:text-5xl text-[var(--rs-ink)] max-w-3xl">Four small principles that quietly run the company.</h2>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {values.map((v, i) => (
              <div key={i} className="rs-card p-7 lift">
                <div className="w-11 h-11 rounded-lg bg-[var(--rs-green)]/10 text-[var(--rs-green)] grid place-items-center">
                  <v.icon className="w-5 h-5" />
                </div>
                <h3 className="h-display text-xl mt-5 text-[var(--rs-ink)]">{v.title}</h3>
                <p className="text-sm text-[var(--rs-ink)]/70 mt-2">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="sect bg-[var(--rs-ink)] text-[var(--rs-cream)]">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 text-center">
          <Quote className="w-9 h-9 text-[var(--rs-coral)] mx-auto mb-5" />
          <p className="h-display text-3xl md:text-4xl leading-snug">“We don’t want to be the biggest. We want to be the one your branch director tells the new starter to use on day one.”</p>
          <div className="mt-6 text-[var(--rs-cream)]/65">Tim Bentley, Operations Manager</div>
        </div>
      </section>

      <CtaBanner />
    </div>
  );
};

export default About;
